/**
 * One curriculum group (業種/コース) on the dashboard progress card.
 *
 * Collapsed: white row with name + stacked WEEK bar + 完了/総WEEK counter + chevron down.
 * Expanded: the same single row, now on the tinted block, chevron flipped up,
 * followed by the WEEK chips with legend and the per-WEEK accordions of that group.
 */

import { FC, memo, useCallback, useMemo, useState } from "react";
import { Collapse, Stack, Typography } from "@mui/material";
import { IconChevronDown } from "@/assets/icons";
import { background, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { TCurriculumGroupDto } from "@/api/services/main/curriculum-progress/dtos/get-curriculum-progress.dto";
import { TCurriculumGroupCounts, countGroupStatuses } from "@/features/dashboard/utils/curriculumGroups";
import { CurriculumProgressBar } from "@/features/dashboard/components/learning-progress/CurriculumProgressBar";
import { WeekStatusChips } from "@/features/dashboard/components/learning-progress/WeekStatusChips";
import { SubjectCurriculumAccordion } from "@/features/dashboard/components/learning-progress/SubjectCurriculumAccordion";

type TCurriculumGroupAccordionProps = {
  group: TCurriculumGroupDto;
  /**
   * WEEK the recommendation API points at — BE already resolves it from the learner's
   * start date (one WEEK per week), so FE just expands its group and highlights it.
   */
  recommendedSubjectId?: number;
  /** Trial learners may only open the first WEEK overall, hence the running offset */
  isTrial?: boolean;
  /** Number of WEEKs rendered by the groups before this one */
  subjectIndexOffset?: number;
};

/**
 * The single toggle row: group name + stacked bar + 完了/総WEEK counter + chevron.
 * Rendered once in both states — the chevron points down when collapsed and up when expanded.
 */
const GroupSummary: FC<{ name: string; counts: TCurriculumGroupCounts; expanded: boolean; onToggle: () => void }> = ({
  name,
  counts,
  expanded,
  onToggle,
}) => (
  <Stack sx={{ px: spacing.sm, gap: spacing.xxs, cursor: "pointer" }} onClick={onToggle}>
    {name && <Typography sx={{ ...typography.body14Semi, color: text.primary }}>{name}</Typography>}

    <Stack direction="row" alignItems="center" gap={spacing.xs}>
      <CurriculumProgressBar counts={counts} />
      <Typography sx={{ ...typography.body14Semi, color: text.secondary, whiteSpace: "nowrap" }}>
        {counts.completed}/{counts.total}
      </Typography>
      <Stack
        sx={{
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
          flexShrink: 0,
        }}
      >
        <IconChevronDown width={24} height={24} />
      </Stack>
    </Stack>
  </Stack>
);

const CurriculumGroupAccordionComponent: FC<TCurriculumGroupAccordionProps> = ({
  group,
  recommendedSubjectId,
  isTrial = false,
  subjectIndexOffset = 0,
}) => {
  const counts = useMemo(() => countGroupStatuses(group.subjects), [group.subjects]);
  /* Always starts collapsed — the learner opens the group they want */
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    /* Collapsed the whole group stays on the card's white; expanded the entire block is tinted */
    <Stack
      sx={{
        borderRadius: rounded.xs,
        bgcolor: expanded ? background.secondary : background.white,
        py: expanded ? spacing.xs : 0,
        gap: spacing.xs,
        transition: "background-color 0.2s",
      }}
    >
      <GroupSummary name={group.name} counts={counts} expanded={expanded} onToggle={handleToggleExpand} />

      <Collapse in={expanded} timeout={200} unmountOnExit>
        <Stack sx={{ px: spacing.sm, gap: spacing.xs }}>
          <WeekStatusChips subjects={group.subjects} counts={counts} />

          {/* Per-WEEK accordions */}
          {group.subjects.map((subject, index) => (
            <SubjectCurriculumAccordion
              key={subject.id}
              subject={subject}
              isRecommended={subject.id === recommendedSubjectId}
              isDisabled={isTrial && subjectIndexOffset + index > 0}
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export const CurriculumGroupAccordion = memo(CurriculumGroupAccordionComponent);
